import { components as c } from "@/components";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { templateQueryOptions } from "@/features/templates/api/selectTemplates";
import NewTemplateCard from "@/features/templates/components/NewTemplateCard";
import TemplateCard from "@/features/templates/components/TemplateCard";
import TemplateSkeleton from "@/features/templates/components/TemplateSkeleton";
import type { Template } from "@/types/database/diaries";
import type { TemplateSettingsSchemaT } from "@/types/zod/quickstart-settings";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { useState } from "react";

function getPaginationItems(current: number, total: number) {
  return Array.from({ length: total }, (_, i) => i + 1);
}

function TemplateList() {
  const { data: templates } = useSuspenseQuery<Template["Row"][]>(
    templateQueryOptions(),
  );

  const [page, setPage] = useState(1);

  // Pagination logic
  const pageSize = 5;
  const totalTemplates = Array.isArray(templates) ? templates.length : 0;
  const totalPages = Math.max(1, Math.ceil(totalTemplates / pageSize));
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const paginatedTemplates = templates.slice(start, end);

  // Ensure we have an array
  if (!Array.isArray(templates)) {
    console.error("templates is not an array:", templates);
    return null;
  }

  return (
    <>
      {paginatedTemplates.map((template) => {
        const ta = (template as unknown as {
          templates_access?: { access: "viewer" | "editor" }[];
        }).templates_access;
        return (
          <TemplateCard
            key={template.template_id}
            template={{
              ...template,
              settings: typeof template.settings === "object" &&
                  template.settings !== null
                ? (template.settings as TemplateSettingsSchemaT)
                : ({} as TemplateSettingsSchemaT),
              templates_access: Array.isArray(ta) ? ta : [],
            }}
          />
        );
      })}
      <div className="flex justify-center col-span-full mx-auto mt-6">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setPage((p) => Math.max(1, p - 1));
                }}
                aria-disabled={page === 1}
              >
                <span className="sr-only">Previous</span>
              </PaginationPrevious>
            </PaginationItem>
            {getPaginationItems(page, totalPages).map((item) =>
              item === "ellipsis"
                ? (
                  <PaginationItem key={`ellipsis-${page}-${totalPages}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                )
                : (
                  <PaginationItem key={`page-${item}`}>
                    <PaginationLink
                      href="#"
                      isActive={page === item}
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(item as number);
                      }}
                    >
                      {item}
                    </PaginationLink>
                  </PaginationItem>
                )
            )}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setPage((p) => Math.min(totalPages, p + 1));
                }}
                aria-disabled={page === totalPages}
              >
                <span className="sr-only lg:not-sr-only">Next</span>
              </PaginationNext>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </>
  );
}

function QRCodeForm() {
  return (
    <>
      <c.cc.Title
        words="Add a New Diary"
        description="To reduce time spend per patient, you can create templates for common diaries which you can use to invite patients by email or simply by showing them the QR code."
      />

      <div className="gap-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mt-8">
        {/* Add New Template Card */}
        <NewTemplateCard />

        {/* Template Cards */}
        <TemplateList />
      </div>
    </>
  );
}

export const Route = createFileRoute("/_app/new/diary/")({
  component: QRCodeForm,
  loader: ({ context }) => {
    context.queryClient.ensureQueryData(templateQueryOptions());
  },
});
